import { Link } from "react-router-dom";
import {
  ArrowLeft,
  CheckCircle2,
  Clock,
  RefreshCcw,
  RotateCcw,
  Slash,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { PACKAGE_LABEL, type OrderRow } from "@/lib/admin-orders-shared";
import { formatPrice } from "@/lib/packages";

import {
  CancelOrderDialog,
  CompleteOrderDialog,
  ExtendDeadlineDialog,
  RefundOrderDialog,
  ReopenOrderDialog,
  StartBuildButton,
  StatusFlipButton,
} from "./action-dialogs";
import { DeadlineCell } from "./deadline-cell";
import { StatusPill } from "./status-pill";

// Extended order type with all fields the detail page needs
export type OrderDetail = OrderRow & {
  contactPhone: string | null;
  startedAt: string | null;
  completedAt: string | null;
  customerCompletedAt: string | null;
  stripePaymentIntentId: string | null;
  projectType: string;
  industry: string | null;
  description: string;
  references: unknown;
  features: unknown;
  pages: unknown;
  brandingData: unknown;
  successCriteria: string | null;
  contactName: string;
  briefSnapshot: unknown;
};

export function OrderHeader({
  order,
  hasDelivery,
}: {
  order: OrderDetail;
  hasDelivery: boolean;
}) {
  const isClosed = order.status === "COMPLETED" || order.status === "CANCELLED";
  const showStartButton = order.status === "PENDING" && order.paidAt !== null;

  return (
    <div className="space-y-4">
      <Link
        to="/admin"
        className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="size-3.5" />
        Back to orders
      </Link>

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="space-y-1">
          <p className="font-mono text-[11px] uppercase tracking-[0.06em] text-muted-foreground">
            {order.orderNumber} · {PACKAGE_LABEL[order.packageType]} · {formatPrice(order.priceUsd)}
          </p>
          <h1 className="font-heading text-2xl font-semibold tracking-tight">
            {order.businessName}
          </h1>
          <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
            <StatusPill status={order.status} />
            {order.paidAt && !isClosed ? (
              <span className="inline-flex items-center gap-1.5 text-xs">
                <Clock className="size-3.5" />
                <DeadlineCell
                  iso={order.deadlineAt}
                  overdue={new Date(order.deadlineAt).getTime() < Date.now() && !isClosed}
                />
              </span>
            ) : null}
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-end gap-2">
          {!isClosed ? (
            <>
              {order.paidAt ? (
                <ExtendDeadlineDialog
                  orderId={order.id}
                  currentDeadline={order.deadlineAt}
                  trigger={
                    <Button variant="outline" size="sm">
                      <Clock className="size-3.5" />
                      Extend deadline
                    </Button>
                  }
                />
              ) : null}

              <CancelOrderDialog
                orderId={order.id}
                isPaid={order.paidAt !== null}
                trigger={
                  <Button variant="outline" size="sm">
                    <Slash className="size-3.5" />
                    Cancel
                  </Button>
                }
              />

              {order.paidAt ? (
                <RefundOrderDialog
                  orderId={order.id}
                  trigger={<Button variant="destructive" size="sm">Refund</Button>}
                />
              ) : null}

              {order.status === "IN_PROGRESS" ? (
                <StatusFlipButton orderId={order.id} from="IN_PROGRESS" to="IN_REVIEW" label="Move to review" variant="outline" />
              ) : null}
              {order.status === "IN_REVIEW" ? (
                <StatusFlipButton orderId={order.id} from="IN_REVIEW" to="IN_PROGRESS" label="Back to in-progress" variant="outline" />
              ) : null}
              {order.status === "REVISIONS_PENDING" ? (
                <StatusFlipButton orderId={order.id} from="REVISIONS_PENDING" to="IN_REVIEW" label="Move to review" variant="outline" />
              ) : null}

              {showStartButton ? <StartBuildButton orderId={order.id} /> : null}

              {(order.status === "IN_PROGRESS" || order.status === "IN_REVIEW" || order.status === "REVISIONS_PENDING") && order.paidAt ? (
                <CompleteOrderDialog
                  orderId={order.id}
                  hasDelivery={hasDelivery}
                  trigger={
                    <Button>
                      <CheckCircle2 className="size-3.5" />
                      Mark complete
                    </Button>
                  }
                />
              ) : null}
            </>
          ) : order.status === "COMPLETED" ? (
            <ReopenOrderDialog
              orderId={order.id}
              trigger={
                <Button variant="outline" size="sm">
                  <RotateCcw className="size-3.5" />
                  Reopen
                </Button>
              }
            />
          ) : (
            <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
              <RefreshCcw className="size-3.5" />
              Closed
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
